import {
    IonButton,
    IonButtons,
    IonCard,
    IonContent,
    IonHeader,
    IonIcon,
    IonFab,
    IonFabButton,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonInput,
    useIonRouter
} from '@ionic/react';
import { arrowForwardOutline, addOutline, chevronBackOutline, chevronForwardOutline, timeOutline, peopleOutline } from 'ionicons/icons';
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './View.css';
import { API_BASE_URL } from '../config/api';
import { buildOrganizationUrl } from '../helpers/organizationUtils';
import Loader from '../components/Loader';

// Helper functions moved outside component to avoid dependency issues
const calculatePollDuration = (timeLeft) => {
    if (!timeLeft) return 0;

    const timeParts = timeLeft.split(', ').map((part) => part.trim());
    let totalMilliseconds = 0;

    timeParts.forEach((part) => {
        if (part.includes('days')) {
            totalMilliseconds += parseInt(part) * 24 * 60 * 60 * 1000;
        } else if (part.includes('hours')) {
            totalMilliseconds += parseInt(part) * 60 * 60 * 1000;
        } else if (part.includes('mins')) {
            totalMilliseconds += parseInt(part) * 60 * 1000;
        }
    });

    return totalMilliseconds;
};

const formatTimeRemaining = (milliseconds) => {
    const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000));
    const hours = Math.floor((milliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));

    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    if (minutes > 0) return `${minutes}m left`;
    return 'Ending soon';
};

const View = () => {
    const router = useIonRouter();
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;
    const [userVotes, setUserVotes] = useState({});
    const location = useLocation();

    // Get filter from query params
    const queryParams = new URLSearchParams(location.search);
    const filter = queryParams.get('filter');

    const fetchPolls = useCallback(async () => {
        setLoading(true);
        const safetyTimeout = setTimeout(() => {
            setLoading(false);
        }, 12000);

        try {
            // Determine API parameters based on filter type
            const apiParams = { include_public: filter === 'org' ? 'false' : 'true' };

            if (filter === 'general') {
                apiParams.action = 'get_general_polls';
                // For general polls, we explicitly want to see Super Admin (Role 1) polls 
                // and public polls, regardless of our own organization.
            } else if (filter === 'org') {
                apiParams.action = 'get_organization_polls';
            }

            const url = buildOrganizationUrl(`${API_BASE_URL}/get_polls_simple.php`, apiParams);
            const response = await axios.get(url, { timeout: 8000 });

            let pollsData = [];
            if (response.data && response.data.success && Array.isArray(response.data.polls)) {
                // New API format
                pollsData = response.data.polls;
            } else if (Array.isArray(response.data)) {
                // Old API format - direct array
                pollsData = response.data;
            } else {
                console.error('Unexpected API response format:', response.data);
                setPolls([]);
                setLoading(false);
                clearTimeout(safetyTimeout);
                return;
            }

            const currentTime = new Date();
            const updatedPolls = pollsData.map((poll) => {
                const pollEndTime = new Date(new Date(poll.created_at).getTime() + calculatePollDuration(poll.time_left));
                const timeRemaining = pollEndTime - currentTime;

                return {
                    ...poll,
                    expired: timeRemaining <= 0,
                    timeRemainingFormatted: timeRemaining > 0 ? formatTimeRemaining(timeRemaining) : 'Expired'
                };
            });

            setPolls(updatedPolls);
        } catch (error) {
            console.error('Error fetching polls:', error);
            setPolls([]);
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    }, [filter, currentPage, searchTerm]);

    const fetchUserVotes = useCallback(async () => {
        const userId = sessionStorage.getItem('user_id');
        if (!userId) return;

        try {
            const response = await axios.get(`${API_BASE_URL}/get_user_votes.php?user_id=${userId}`, { timeout: 8000 });
            if (response.data && typeof response.data === 'object') {
                setUserVotes(response.data);
            }
        } catch (error) {
            console.error('Error fetching user votes:', error);
        }
    }, []);

    useEffect(() => {
        if (!sessionStorage.getItem('user_id')) {
            router.push('/page/login', 'root', 'replace');
            return;
        }

        fetchPolls();
        fetchUserVotes();
    }, [router, fetchPolls, fetchUserVotes, filter]); // Re-fetch when filter changes

    // Listen for poll creation events to auto-refresh
    useEffect(() => {
        const handlePollCreated = () => {
            console.log('Poll created event received, refreshing polls...');
            fetchPolls();
        };

        window.addEventListener('pollCreated', handlePollCreated);

        return () => {
            window.removeEventListener('pollCreated', handlePollCreated);
        };
    }, [fetchPolls]);

    const filteredPolls = polls.filter(poll =>
        poll.question.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredPolls.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedPolls = filteredPolls.slice(startIndex, endIndex);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            // Scroll to top of grid
            document.querySelector('.polls-grid')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <div className="title-container">
                            <span className="title-text">
                                {filter === 'org' ? 'My Organization Polls' :
                                    filter === 'general' ? 'General Polls' :
                                        'View Polls'}
                            </span>
                        </div>
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader message="Fetching Polls..." />}
                <div className="polls-container">
                    {/* Search Bar */}
                    <div className="search-container">
                        <IonInput
                            value={searchTerm}
                            placeholder="Search polls..."
                            onIonInput={(e) => setSearchTerm(e.detail.value)}
                            className="search-input"
                        />
                    </div>

                    {/* Polls Grid */}
                    <div className="polls-grid">
                        {paginatedPolls.length > 0 ? (
                            paginatedPolls.map((poll, index) => (
                                <IonCard
                                    key={poll.id || index}
                                    className={`poll-card ${poll.expired ? 'expired-card' : ''}`}
                                    routerLink={poll.expired ? undefined : `/page/view/${poll.id}`}
                                    style={{ '--poll-color': poll.color }}
                                >
                                    <div className="poll-card-content">
                                        <h3 className="poll-question">{poll.question}</h3>

                                        <div className="poll-meta">
                                            <span className="poll-time">
                                                <IonIcon icon={timeOutline} style={{ fontSize: '0.9rem' }} />
                                                {poll.timeRemainingFormatted}
                                            </span>
                                            <span className="poll-votes">
                                                <IonIcon icon={peopleOutline} style={{ fontSize: '0.9rem' }} />
                                                {poll.totalvotes || 0} votes
                                            </span>
                                        </div>

                                        <div className="poll-status">
                                            {userVotes[poll.id] ? (
                                                <span className="voted-badge">✓ Voted</span>
                                            ) : (
                                                <span className="not-voted-badge">Vote Now</span>
                                            )}
                                        </div>

                                        <IonButton
                                            fill="clear"
                                            size="small"
                                            className={`view-button ${poll.expired ? 'button-disabled' : ''}`}
                                            disabled={poll.expired}
                                        >
                                            View Details
                                            <IonIcon icon={arrowForwardOutline} slot="end" />
                                        </IonButton>
                                    </div>
                                    <div className={`poll-accent-bar ${poll.expired ? 'accent-expired' : 'accent-active'}`} style={!poll.expired ? { background: poll.color } : {}}></div>
                                </IonCard>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="empty-state-icon">📊</div>
                                <h3>No polls found</h3>
                                <p>
                                    {searchTerm ? 'Try adjusting your search terms' : 'Be the first to create a poll!'}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            padding: '12px 16px',
                            marginTop: '1.5rem',
                            marginBottom: '1rem',
                            gap: '8px',
                            width: '100%',
                            maxWidth: '500px',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronBackOutline} slot="icon-only" />
                                </IonButton>

                                {[...Array(totalPages)].map((_, i) => (
                                    <IonButton
                                        key={i + 1}
                                        fill={currentPage === i + 1 ? 'solid' : 'outline'}
                                        size="small"
                                        onClick={() => goToPage(i + 1)}
                                        style={{
                                            '--border-radius': '8px',
                                            minWidth: '36px',
                                            display: (totalPages <= 5 || i < 2 || i >= totalPages - 2 || Math.abs(i + 1 - currentPage) <= 1) ? 'inline-flex' : 'none'
                                        }}
                                    >
                                        {i + 1}
                                    </IonButton>
                                ))}

                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronForwardOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
                                Showing {startIndex + 1} - {Math.min(endIndex, filteredPolls.length)} of {filteredPolls.length} polls
                            </div>
                        </div>
                    )}
                </div>

                {/* Floating Action Button - Visible only for Super Admin (1) and Org Admin (2) */}
                {(([...['1', '2', 1, 2]].includes(sessionStorage.getItem('role_id')) ||
                    [...['1', '2', 1, 2]].includes(localStorage.getItem('roleId'))) && (
                        <IonFab vertical="bottom" horizontal="end" slot="fixed">
                            <IonFabButton routerLink="/page/add" className="fab-button">
                                <IonIcon icon={addOutline} />
                            </IonFabButton>
                            <div className="fab-label">Add New Poll</div>
                        </IonFab>
                    ))}
            </IonContent>
        </IonPage>
    );
};

export default View;
